import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CreditCard, X } from 'lucide-react';
import { formatDate, formatMoney } from '../utils/locale';

const SEEN_KEY = 'billing_due_popup_seen';

// Every login (or institute switch) issues a new token, so a fingerprint of it marks one
// session: the popup shows once after login and stays closed across reloads until the next one.
const sessionFingerprint = () => {
  let token = '';
  try { token = localStorage.getItem('token') || ''; } catch { return ''; }
  let hash = 5381;
  for (let i = 0; i < token.length; i += 1) hash = ((hash * 33) ^ token.charCodeAt(i)) >>> 0;
  return token ? hash.toString(36) : '';
};

const wasSeen = (fingerprint) => {
  try { return Boolean(fingerprint) && localStorage.getItem(SEEN_KEY) === fingerprint; } catch { return false; }
};

export default function BillingDueBanner({ role, summary }) {
  const account = summary?.account_summary;
  const amount = Number(account?.total_payable || 0);
  const fingerprint = sessionFingerprint();
  const [dismissed, setDismissed] = useState(() => wasSeen(fingerprint));
  const overdue = ['overdue', 'restricted'].includes(summary?.billing_state);
  const isVisible = role === 'institute_admin' && Boolean(summary?.contract?.id) && amount > 0 && !dismissed;

  useEffect(() => { setDismissed(wasSeen(fingerprint)); }, [fingerprint]);

  const dismiss = () => {
    try { if (fingerprint) localStorage.setItem(SEEN_KEY, fingerprint); } catch { /* storage blocked: closes for this page only */ }
    setDismissed(true);
  };

  useEffect(() => {
    if (!isVisible) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape') dismiss();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isVisible) return null;

  const memberCount = Number(account?.pending_heads_count || 0);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm"
      onClick={dismiss}
      data-testid="billing-due-modal"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-due-title"
        className="w-full max-w-lg overflow-hidden rounded-3xl border border-amber-200 bg-white shadow-2xl dark:border-amber-500/30 dark:bg-slate-900"
        onClick={event => event.stopPropagation()}
      >
        <div className={`h-1.5 ${overdue ? 'bg-rose-500' : 'bg-amber-400'}`} />
        <div className="p-6 sm:p-7">
          <div className="flex items-start gap-4">
            <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${overdue ? 'bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'}`}>
              <AlertTriangle className="h-6 w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 id="billing-due-title" className="font-outfit text-lg font-bold text-slate-900 dark:text-white">
                {overdue ? 'Platform bill overdue' : 'Pending platform bill'}
              </h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                {memberCount > 0 ? `${memberCount} billable ${memberCount === 1 ? 'member is' : 'members are'} included in the outstanding bill.` : 'Your institute has an outstanding platform bill.'}
              </p>
            </div>
            <button type="button" onClick={dismiss} aria-label="Close billing reminder" className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className={`mt-6 rounded-2xl border p-4 ${overdue ? 'border-rose-200 bg-rose-50 dark:border-rose-500/20 dark:bg-rose-500/10' : 'border-amber-200 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/10'}`}>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Total payable</p>
            <p className="mt-1 font-outfit text-3xl font-extrabold text-slate-900 dark:text-white">{formatMoney(amount)}</p>
            {account?.oldest_due_date && <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">Oldest bill due {formatDate(account.oldest_due_date)}</p>}
          </div>

          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={dismiss} className="h-11 rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">
              Close
            </button>
            <Link to="/institute-panel/billing" onClick={dismiss} className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 text-sm font-bold text-white transition hover:bg-indigo-700">
              <CreditCard className="h-4 w-4" /> View bill & pay
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
